import { JustificationFilterOptions } from '@/Models/GenericWorklist/JustificationFilterOptions';
import PatientOriginFilter from '@/Models/GenericWorklist/PatientOriginFilter';
import PatientStatusFilter from '@/Models/GenericWorklist/PatientsStatusFilter';
import type Status from '@/Models/Shared/Status';
import { assertUnreachable } from '@/Utility/Assertions/assertUnreachable';
import { DobDate, DobMultiselect, DobSelect } from '@dedalusdiit/dob-components';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useMemo } from 'react';
import type WorklistSidebarStore from '../WorklistSidebarStore';
import DebouncedFilterInput from './DebouncedFilterInput';
import StudyDateFilter from './StudyDateFilter';
import WorklistFilterHelper from './WorklistFilterHelper';
import Style from './WorklistFilters.module.scss';
import { DobMultiSelectOption } from '@/Pages/GenericWorklist/Components/Sidebar/Filters/DobMultiSelectOption';
import { getInvoiceStatusString } from '@/Components/Invoicing/InvoiceStatus';
import { getReportStatusFilterString } from '@/Models/GenericWorklist/ReportStatusFilter';
import { getReportParticipantTypeLabel } from '@/Models/GenericWorklist/ReportParticipantType';
import { t } from 'i18next';
import { getTitleForWorklistProperty, WorklistFilterProperty } from '@/Pages/GenericWorklist/Logic/WorklistProperties';

const { filterInputs: filterInputsStyle } = Style;

interface WorklistFiltersProps {
    sideBarStore: WorklistSidebarStore;
}

// The filter renderers are split into three groups purely to keep each switch below the
// maximum number of cases. The split is exhaustive: any WorklistFilterProperty that is not
// listed below falls into ReportFilterProperty, where the missing switch case becomes a
// compile error via assertUnreachable.
const PATIENT_FILTER_PROPERTIES = ['PatientFirstName', 'PatientLastName', 'DoB', 'ExternalID', 'NationalNumber', 'Language', 'PatientStatus', 'PatientOrigin', 'VisitNumber', 'UnitOfCare'] as const;

const ORDER_FILTER_PROPERTIES = ['RoomsIDs', 'SiteIDs', 'OrderStatus', 'ProcedureStatus', 'LabelsToIncludeIDs', 'LabelsToExcludeIDs', 'UrgencyIds', 'DateRange', 'AccessionNumber', 'Radiologists', 'Technicians', 'ProcedureDescription', 'ModalityIDs', 'ProcedureCodes', 'OrderNumber', 'RequestingPhysicianFirstName', 'RequestingPhysicianLastName'] as const;

type PatientFilterProperty = typeof PATIENT_FILTER_PROPERTIES[number];
type OrderFilterProperty = typeof ORDER_FILTER_PROPERTIES[number];
type ReportFilterProperty = Exclude<WorklistFilterProperty, PatientFilterProperty | OrderFilterProperty>;

const isPatientFilterProperty = (datapoint: WorklistFilterProperty): datapoint is PatientFilterProperty =>
    (PATIENT_FILTER_PROPERTIES as readonly WorklistFilterProperty[]).includes(datapoint);

const isOrderFilterProperty = (datapoint: WorklistFilterProperty): datapoint is OrderFilterProperty =>
    (ORDER_FILTER_PROPERTIES as readonly WorklistFilterProperty[]).includes(datapoint);

const WorklistFilters = (props: WorklistFiltersProps): React.ReactElement => {
    const { sideBarStore } = props;
    const { worklistViewConfig } = sideBarStore;

    const worklistFilterHelper = useMemo(() => new WorklistFilterHelper(sideBarStore), [sideBarStore]);

    const filtersDisabled = worklistFilterHelper.filtersAreDisabled;
    const firstNameFilterValue = worklistFilterHelper.patientFirstNameFilterValue;
    const patientDoBFilterValue = worklistFilterHelper.patientDoBFilterValue;
    const visitNumberValue = worklistFilterHelper.visitNumberValue;
    const externalIDFilterValue = worklistFilterHelper.patientExternalIDFilterValue;
    const nationalNumberFilterValue = worklistFilterHelper.patientNationalNumberFilterValue;
    const unitOfCareFilterValue = worklistFilterHelper.unitOfCareFilterValue;
    const dateRangePreset = worklistFilterHelper.dateRangePreset;
    const from = worklistFilterHelper.from;
    const until = worklistFilterHelper.until;
    const accessionNumberFilterValue = worklistFilterHelper.accessionNumberFilterValue;
    const procedureDescriptionFilterValue = worklistFilterHelper.procedureDescriptionFilterValue;
    const orderNumberFilterValue = worklistFilterHelper.orderNumberFilterValue;
    const requestingPhysicianFirstNameFilterValue = worklistFilterHelper.requestingPhysicianFirstNameFilterValue;
    const requestingPhysicianLastNameFilterValue = worklistFilterHelper.requestingPhysicianLastNameFilterValue;

    const onFirstNameChange = useCallback((newValue: string): void => {
        worklistFilterHelper.setPatientFirstNameSearchTermFilter(newValue);
    }, [worklistFilterHelper]);

    const onOrderNumberChange = useCallback((newValue: string): void => {
        worklistFilterHelper.setOrderNumberSearchTermFilter(newValue);
    }, [worklistFilterHelper]);

    const onRequestingPhysicianFirstNameChange = useCallback((newValue: string): void => {
        worklistFilterHelper.setRequestingPhysicianFirstNameSearchTermFilter(newValue);
    }, [worklistFilterHelper]);

    const onRequestingPhysicianLastNameChange = useCallback((newValue: string): void => {
        worklistFilterHelper.setRequestingPhysicianLastNameSearchTermFilter(newValue);
    }, [worklistFilterHelper]);

    const onUnitOfCareChange = useCallback((newValue: string): void => {
        worklistFilterHelper.setUnitOfCareSearchTermFilter(newValue);
    }, [worklistFilterHelper]);

    const onDoBChange = (newValue: Date | null): void => {
        worklistFilterHelper.setDoBSearchTermFilter(newValue);
    };

    const onVisitNumberChange = useCallback((newValue: string): void => {
        worklistFilterHelper.setVisitNumberFilter(newValue);
    }, [worklistFilterHelper]);

    const onExternalIDChange = useCallback((newValue: string): void => {
        worklistFilterHelper.setExternalIDTermFilter(newValue);
    }, [worklistFilterHelper]);

    const onNationalNumberChange = useCallback((newValue: string): void => {
        worklistFilterHelper.setNationalNumberTermFilter(newValue);
    }, [worklistFilterHelper]);

    const lastNameFilterValue = worklistFilterHelper.patientLastNameFilterValue;
    const onLastNameChange = useCallback((newValue: string): void => {
        worklistFilterHelper.setPatientLastNameSearchTermFilter(newValue);
    }, [worklistFilterHelper]);

    const onDateRangeChange = (update?: { from?: Date; until?: Date; preset?: string }): void => {
        if (update?.preset) {
            worklistFilterHelper.setDateRangePreset(update.preset);
        }
        else {
            worklistFilterHelper.setDateRange(update?.from, update?.until);
        }
    };

    const reportCreatedDateRangePreset = worklistFilterHelper.reportCreatedDateRangePreset;
    const reportCreatedFrom = worklistFilterHelper.reportCreatedFrom;
    const reportCreatedUntil = worklistFilterHelper.reportCreatedUntil;

    const reportModifiedDateRangePreset = worklistFilterHelper.reportModifiedDateRangePreset;
    const reportModifiedFrom = worklistFilterHelper.reportModifiedFrom;
    const reportModifiedUntil = worklistFilterHelper.reportModifiedUntil;

    const onAccessionNumberChange = useCallback((newValue: string): void => {
        worklistFilterHelper.setAccessionNumberSearchTermFilter(newValue);
    }, [worklistFilterHelper]);

    const onProcedureDescriptionChange = useCallback((newValue: string): void => {
        worklistFilterHelper.setProcedureDescriptionSearchTermFilter(newValue);
    }, [worklistFilterHelper]);

    const allSitesMultiSelectOptions = worklistFilterHelper.allSites.map((site) => ({ value: site.ID, label: site.Name }));
    const selectedSitesMultiSelectOptions = worklistFilterHelper.selectedSites.map((site) => ({ value: site.ID, label: site.Name }));

    const allStatusesMultiSelectOptions = worklistFilterHelper.allPatientStatuses.map((status) => ({ value: status, label: PatientStatusFilter[status] }));
    const selectedStatusesMultiSelectOptions = worklistFilterHelper.selectedStatuses.map((status) => ({ value: status, label: PatientStatusFilter[status] }));

    const allPatientOriginsMultiSelectOptions = worklistFilterHelper.allPatientOrigins.map((origin) => ({ value: origin, label: PatientOriginFilter[origin] }));
    const selectedPatientOriginsMultiSelectOptions = worklistFilterHelper.selectedPatientOrigins.map((origin) => ({ value: origin, label: PatientOriginFilter[origin] }));

    const allPatientLanguagesMultiSelectOptions = worklistFilterHelper.allPatientLanguages.map((language) => ({ value: language.ID, label: language.Name }));
    const selectedPatientLanguagesMultiSelectOptions = worklistFilterHelper.selectedPatientLanguages.map((language) => ({ value: language.ID, label: language.Name }));

    const allOrderStatusesMultiSelectOptions = worklistFilterHelper.allOrderStatuses.map((status) => ({ value: status.ID, label: t(status.Name) }));
    const selectedOrderStatusesMultiSelectOptions = worklistFilterHelper.selectedOrderStatuses.map((status) => ({ value: status.ID, label: t(status.Name) }));

    const allLabelsToExcludeMultiSelectOptions = worklistFilterHelper.allLabelsToExclude.map((label) => ({
        ID: label.ID,
        Value: label.Name,
        Disabled: worklistFilterHelper.selectedLabelsToInclude.some((included) => included.ID === label.ID),
    }));

    const selectedLabelsToExcludeMultiSelectOptions = worklistFilterHelper.selectedLabelsToExclude.map((label) => ({
        ID: label.ID,
        Value: label.Name,
        Disabled: worklistFilterHelper.selectedLabelsToInclude.some((included) => included.ID === label.ID),
    }));

    const allLabelsToIncludeMultiSelectOptions = worklistFilterHelper.allLabelsToInclude.map((label) => ({
        ID: label.ID,
        Value: label.Name,
        Disabled: worklistFilterHelper.selectedLabelsToExclude.some((excluded) => excluded.ID === label.ID),
    }));

    const selectedLabelsToIncludeMultiSelectOptions = worklistFilterHelper.selectedLabelsToInclude.map((label) => ({
        ID: label.ID,
        Value: label.Name,
        Disabled: worklistFilterHelper.selectedLabelsToExclude.some((excluded) => excluded.ID === label.ID),
    }));

    const allUrgenciesMultiSelectOptions = worklistFilterHelper.allUrgencies.map((urgency) => ({
        ID: urgency.ID,
        Value: urgency.Name,
        Disabled: false,
    }));

    const selectedUrgenciesMultiSelectOptions = worklistFilterHelper.selectedUrgencies.map((urgency) => ({
        ID: urgency.ID,
        Value: urgency.Name,
        Disabled: false,
    }));

    const allProcedureStatusMultiSelectOptions = worklistFilterHelper.allProcedureStatuses.map((status) => ({ value: status.ID, label: t(status.Name) }));
    const selectedProcedureStatusMultiSelectOptions = worklistFilterHelper.selectedProcedureStatuses.map((status) => ({ value: status.ID, label: t(status.Name) }));

    const allModalitiesMultiSelectOptions = worklistFilterHelper.allModalities.map((modality) => ({ value: modality.ID, label: modality.Name }));
    const selectedModalitiesMultiSelectOptions = worklistFilterHelper.selectedModalities.map((modality) => ({ value: modality.ID, label: modality.Name }));

    const selectedRoomsMultiselectOptions = worklistFilterHelper.selectedRooms.map((room) => ({ value: room.ID, label: room.Name }));
    const visibleRoomsMultiSelectOptions = worklistFilterHelper.visibleRoomOptions;

    const selectedProcedureCodesMultiSelectOptions = worklistFilterHelper.selectedProcedureCodes?.map((code) => ({ value: code, label: code }));
    const selectedRadiologistsMultiSelectOptions = worklistFilterHelper.selectedRadiologists?.map((radiologist) => ({ value: radiologist.ID, label: radiologist.FullName }));
    const selectedTechniciansMultiSelectOptions = worklistFilterHelper.selectedTechnicians?.map((technician) => ({ value: technician.ID, label: technician.FullName }));

    const allInvoiceStatusesMultiSelectOptions = worklistFilterHelper.allInvoiceStatuses.map(
        (status) => ({ value: status, label: getInvoiceStatusString(Number(status)) }),
    );

    const selectedInvoiceStatusesMultiSelectOptions = worklistFilterHelper.selectedInvoiceStatuses.map(
        (status) => ({ value: status, label: getInvoiceStatusString(Number(status)) }),
    );

    const allReportStatusesMultiSelectOptions = worklistFilterHelper.allReportStatuses.map(
        (status) => ({ value: status, label: getReportStatusFilterString(Number(status)) }),
    );

    const selectedReportStatusesMultiSelectOptions = worklistFilterHelper.selectedReportStatuses.map(
        (status) => ({ value: status, label: getReportStatusFilterString(Number(status)) }),
    );

    const readyForInvoicingOptions = worklistFilterHelper.readyForInvoicingOptions;
    const readyForInvoicingFilterValue = worklistFilterHelper.readyForInvoicingFilterValue;
    const handleReadyForInvoicingChange = (option: { label: string; value: string } | null): void => {
        if (!option) {
            return;
        }
        worklistFilterHelper.setReadyForInvoicingFilter(option.value);
    };
    const reportLockedOptions = worklistFilterHelper.reportLockedOptions;
    const reportLockedFilterValue = worklistFilterHelper.reportLockedFilterValue;
    const handleReportLockedChange = (option: { label: string; value: string } | null): void => {
        if (!option) {
            return;
        }
        worklistFilterHelper.setReportLockedFilter(option.value);
    };
    const reportAvailableOptions = worklistFilterHelper.reportAvailableOptions;
    const reportAvailableFilterValue = worklistFilterHelper.reportAvailableFilterValue;
    const handleReportAvailableChange = (option: { label: string; value: string } | null): void => {
        if (!option) {
            return;
        }
        worklistFilterHelper.setReportAvailableFilter(option.value);
    };
    const justificationOptions = worklistFilterHelper.justificationOptions;
    const justificationFilterValue = worklistFilterHelper.justificationFilterValue;
    const handleJustificationChange = (option: { label: string; value: JustificationFilterOptions } | null): void => {
        if (!option) {
            return;
        }
        worklistFilterHelper.setJustificationFilter(option.value);
    };
    const setSelectedInvoiceStatuses = (statuses: { value: number; label: string }[]): void => {
        worklistFilterHelper.setSelectedInvoiceStatuses(statuses.map((x) => x.value));
    };

    const setSelectedReportStatuses = (statuses: { value: number; label: string }[]): void => {
        worklistFilterHelper.setSelectedReportStatuses(statuses.map((x) => x.value));
    };

    const setSelectedSites = (sites: { value: number; label: string }[]): void => {
        worklistFilterHelper.setSelectedSites(sites.map((x) => x.value));
    };

    const setSelectedProcedureStatuses = (statuses: { value: number; label: string }[]): void => {
        worklistFilterHelper.setSelectedProcedureStatuses(statuses.map((x) => x.value as Status));
    };

    const setSelectedPatientStatuses = (statuses: { value: number; label: string }[]): void => {
        worklistFilterHelper.setSelectedPatientStatuses(statuses.map((x) => x.value));
    };

    const setSelectedOrderStatuses = (statuses: { value: number; label: string }[]): void => {
        worklistFilterHelper.setSelectedOrderStatuses(statuses.map((x) => x.value as Status));
    };

    const setSelectedPatientOrigins = (origins: { value: number; label: string }[]): void => {
        worklistFilterHelper.setSelectedPatientOrigins(origins.map((x) => x.value));
    };

    const setSelectedPatientLanguages = (languages: { value: number; label: string }[]): void => {
        worklistFilterHelper.setSelectedPatientLanguages(languages.map((x) => x.value));
    };

    const setSelectedLabelsToInclude = (labels: DobMultiSelectOption[]): void => {
        worklistFilterHelper.setSelectedLabelsToInclude(labels.map((x) => x.ID as number));
    };

    const setSelectedLabelsToExclude = (labels: DobMultiSelectOption[]): void => {
        worklistFilterHelper.setSelectedLabelsToExclude(labels.map((x) => x.ID as number));
    };

    const setSelectedRooms = (room: { value: number; label: string }[]): void => {
        worklistFilterHelper.setSelectedRooms(room.map((x) => x.value));
    };

    const setSelectedModalities = (modality: { value: number; label: string }[]): void => {
        worklistFilterHelper.setSelectedModalities(modality.map((x) => x.value));
    };

    const setSelectedProcedureCodes = (code: { value: string; label: string }[]): void => {
        worklistFilterHelper.setSelectedProcedureCodes(code.map((x) => x.value));
    };

    const setSelectedRadiologists = (radiologists: { value: number; label: string }[]): void => {
        worklistFilterHelper.setSelectedRadiologists(radiologists.map((r) => ({
            ID: r.value,
            FullName: r.label,
        })),
        );
    };

    const setSelectedTechnicians = (technicians: { value: number; label: string }[]): void => {
        worklistFilterHelper.setSelectedTechnicians(technicians.map((t) => {
            return ({
                ID: t.value,
                FullName: t.label,
            });
        }));
    };

    const selectedReportModifiedByMultiSelectOptions = worklistFilterHelper.selectedReportModifiedBy?.map(
        (user) => ({ value: user.ID, label: user.FullName }),
    );
    const setSelectedReportModifiedBy = (users: { value: number; label: string }[]): void => {
        worklistFilterHelper.setSelectedReportModifiedBy(users.map((u) => ({
            ID: u.value,
            FullName: u.label,
        })));
    };

    const selectedTranscriptionistsMultiSelectOptions = worklistFilterHelper.selectedTranscriptionists?.map(
        (user) => ({ value: user.ID, label: user.FullName }),
    );

    const setSelectedTranscriptionists = (users: { value: number; label: string }[]): void => {
        worklistFilterHelper.setSelectedTranscriptionists(users.map((u) => ({
            ID: u.value,
            FullName: u.label,
        })));
    };

    const setSelectedUrgencies = (urgencies: DobMultiSelectOption[]): void => {
        worklistFilterHelper.setSelectedUrgencies(urgencies.map((x) => x.ID as number));
    };

    const selectedReportParticipantsOptions = worklistFilterHelper.selectedReportParticipants?.map(
        (user) => ({ value: user.ID, label: user.FullName }),
    );

    const setSelectedReportParticipants = (users: { value: number; label: string }[]): void => {
        worklistFilterHelper.setReportParticipants(users.map((u) => ({
            ID: u.value,
            FullName: u.label,
        })));
    };

    const allReportParticipantTypeOptions = worklistFilterHelper.allReportParticipantTypes.map(
        (type) => ({ value: type, label: getReportParticipantTypeLabel(Number(type)) }),
    );

    const selectedReportParticipantTypeOptions = worklistFilterHelper.selectedReportParticipantTypes.map(
        (type) => ({ value: type, label: getReportParticipantTypeLabel(Number(type)) }),
    );

    const setSelectedReportParticipantTypes = (types: { value: number; label: string }[]): void => {
        worklistFilterHelper.setSelectedReportParticipantTypes(types.map((x) => x.value));
    };

    const renderPatientFilter = (datapoint: PatientFilterProperty, label: string): React.ReactElement => {
        switch (datapoint) {
            case 'PatientFirstName':
                return (
                    <DebouncedFilterInput
                        key={datapoint}
                        label={label}
                        onValueChange={onFirstNameChange}
                        disabled={filtersDisabled}
                        value={firstNameFilterValue}
                        onEnter={() => sideBarStore.applyCurrentFilter()}
                    />
                );
            case 'PatientLastName':
                return (
                    <DebouncedFilterInput
                        key={datapoint}
                        label={label}
                        onValueChange={onLastNameChange}
                        disabled={filtersDisabled}
                        value={lastNameFilterValue}
                        onEnter={() => sideBarStore.applyCurrentFilter()}
                    />
                );
            case 'DoB':
                return (
                    <DobDate
                        direction="vertical"
                        key={datapoint}
                        label={label}
                        onChange={(value: string | Date | null) => {
                            const date = value as Date | null;
                            onDoBChange(date);
                        }}
                        selectedDate={patientDoBFilterValue}
                        clearable={true}
                        type="filter"
                    />
                );
            case 'ExternalID':
                return (
                    <DebouncedFilterInput
                        key={datapoint}
                        label={label}
                        onValueChange={onExternalIDChange}
                        disabled={filtersDisabled}
                        value={externalIDFilterValue}
                        onEnter={() => sideBarStore.applyCurrentFilter()}
                    />
                );
            case 'NationalNumber':
                return (
                    <DebouncedFilterInput
                        key={datapoint}
                        label={label}
                        onValueChange={onNationalNumberChange}
                        disabled={filtersDisabled}
                        value={nationalNumberFilterValue}
                        onEnter={() => sideBarStore.applyCurrentFilter()}
                    />
                );
            case 'Language':
                return (
                    <DobMultiselect
                        direction="vertical"
                        disabled={filtersDisabled}
                        placeholder=""
                        key={datapoint}
                        label={label}
                        type="filter"
                        onChange={setSelectedPatientLanguages}
                        options={allPatientLanguagesMultiSelectOptions}
                        optionsSelected={selectedPatientLanguagesMultiSelectOptions}
                    />
                );
            case 'PatientStatus':
                return (
                    <DobMultiselect
                        direction="vertical"
                        disabled={filtersDisabled}
                        placeholder=""
                        key={datapoint}
                        label={label}
                        type="filter"
                        onChange={setSelectedPatientStatuses}
                        options={allStatusesMultiSelectOptions}
                        optionsSelected={selectedStatusesMultiSelectOptions}
                    />
                );
            case 'PatientOrigin':
                return (
                    <DobMultiselect
                        direction="vertical"
                        disabled={filtersDisabled}
                        placeholder=""
                        key={datapoint}
                        label={label}
                        type="filter"
                        onChange={setSelectedPatientOrigins}
                        options={allPatientOriginsMultiSelectOptions}
                        optionsSelected={selectedPatientOriginsMultiSelectOptions}
                    />
                );
            case 'VisitNumber':
                return (
                    <DebouncedFilterInput
                        key={datapoint}
                        label={label}
                        onValueChange={onVisitNumberChange}
                        disabled={filtersDisabled}
                        value={visitNumberValue}
                        onEnter={() => sideBarStore.applyCurrentFilter()}
                    />
                );
            case 'UnitOfCare':
                return (
                    <DebouncedFilterInput
                        key={datapoint}
                        label={label}
                        onValueChange={onUnitOfCareChange}
                        disabled={filtersDisabled}
                        value={unitOfCareFilterValue}
                        onEnter={() => sideBarStore.applyCurrentFilter()}
                    />
                );
            default:
                return assertUnreachable(datapoint);
        }
    };

    const renderOrderFilter = (datapoint: OrderFilterProperty, label: string): React.ReactElement => {
        switch (datapoint) {
            case 'RoomsIDs':
                return (
                    <DobMultiselect
                        direction="vertical"
                        disabled={filtersDisabled}
                        placeholder=""
                        key={datapoint}
                        label={label}
                        type="filter"
                        onChange={setSelectedRooms}
                        options={visibleRoomsMultiSelectOptions}
                        optionsSelected={selectedRoomsMultiselectOptions}
                    />
                );
            case 'SiteIDs':
                return (
                    <DobMultiselect
                        direction="vertical"
                        disabled={filtersDisabled}
                        placeholder=""
                        key={datapoint}
                        label={label}
                        type="filter"
                        onChange={setSelectedSites}
                        options={allSitesMultiSelectOptions}
                        optionsSelected={selectedSitesMultiSelectOptions}
                    />
                );
            case 'OrderStatus':
                return (
                    <DobMultiselect
                        type="filter"
                        label={label}
                        placeholder=""
                        direction="vertical"
                        disabled={filtersDisabled}
                        key={datapoint}
                        onChange={setSelectedOrderStatuses}
                        options={allOrderStatusesMultiSelectOptions}
                        optionsSelected={selectedOrderStatusesMultiSelectOptions}
                    />
                );
            case 'ProcedureStatus':
                return (
                    <DobMultiselect
                        direction="vertical"
                        disabled={filtersDisabled}
                        placeholder=""
                        key={datapoint}
                        label={label}
                        type="filter"
                        onChange={setSelectedProcedureStatuses}
                        options={allProcedureStatusMultiSelectOptions}
                        optionsSelected={selectedProcedureStatusMultiSelectOptions}
                    />
                );
            case 'LabelsToIncludeIDs':
                return (
                    <DobMultiselect
                        key={datapoint}
                        type="filter"
                        label={label}
                        disabled={filtersDisabled}
                        placeholder=""
                        options={allLabelsToIncludeMultiSelectOptions}
                        optionsSelected={selectedLabelsToIncludeMultiSelectOptions}
                        isOptionDisabled={(option) => option.Disabled}
                        optionLabel="Value"
                        optionValue="ID"
                        onChange={(selectedOptions) => setSelectedLabelsToInclude(selectedOptions)}
                    />
                );
            case 'LabelsToExcludeIDs':
                return (
                    <DobMultiselect
                        key={datapoint}
                        type="filter"
                        label={label}
                        disabled={filtersDisabled}
                        placeholder=""
                        options={allLabelsToExcludeMultiSelectOptions}
                        optionsSelected={selectedLabelsToExcludeMultiSelectOptions}
                        isOptionDisabled={(option) => option.Disabled}
                        optionLabel="Value"
                        optionValue="ID"
                        onChange={(selectedOptions) => setSelectedLabelsToExclude(selectedOptions)}
                    />
                );
            case 'UrgencyIds':
                return (
                    <DobMultiselect
                        key={datapoint}
                        type="filter"
                        label={label}
                        disabled={filtersDisabled}
                        placeholder=""
                        options={allUrgenciesMultiSelectOptions}
                        optionsSelected={selectedUrgenciesMultiSelectOptions}
                        isOptionDisabled={(option) => option.Disabled}
                        optionLabel="Value"
                        optionValue="ID"
                        onChange={(selectedOptions) => setSelectedUrgencies(selectedOptions)}
                    />
                );
            case 'DateRange':
                return (
                    <StudyDateFilter
                        filterIsActive={sideBarStore.selectedSpecificFilter === 'DateRange'}
                        label={label}
                        key={datapoint}
                        selectedFromDate={from}
                        selectedUntilDate={until}
                        selectedPreset={dateRangePreset}
                        onChange={onDateRangeChange}
                        setStudyDateFilterActive={(studyDateIsActive) => sideBarStore.setSpecificFilter(studyDateIsActive ? 'DateRange' : undefined)}
                    />
                );
            case 'AccessionNumber':
                return (
                    <DebouncedFilterInput
                        key={datapoint}
                        label={label}
                        onValueChange={onAccessionNumberChange}
                        disabled={filtersDisabled}
                        value={accessionNumberFilterValue}
                        onEnter={() => sideBarStore.applyCurrentFilter()}
                    />
                );
            case 'Radiologists':
                return (
                    <DobMultiselect
                        async
                        direction="vertical"
                        disabled={filtersDisabled}
                        placeholder=""
                        key={datapoint}
                        label={label}
                        type="filter"
                        onChange={setSelectedRadiologists}
                        optionsSelected={selectedRadiologistsMultiSelectOptions}
                        loadOptions={(searchTerm) => worklistFilterHelper.loadRadiologists(searchTerm, t('CurrentUser'))}
                    />
                );
            case 'Technicians':
                return (
                    <DobMultiselect
                        async
                        direction="vertical"
                        disabled={filtersDisabled}
                        placeholder=""
                        key={datapoint}
                        label={label}
                        type="filter"
                        onChange={setSelectedTechnicians}
                        optionsSelected={selectedTechniciansMultiSelectOptions}
                        loadOptions={(searchTerm) => worklistFilterHelper.loadTechnicians(searchTerm)}
                    />
                );
            case 'ProcedureDescription':
                return (
                    <DebouncedFilterInput
                        key={datapoint}
                        label={label}
                        onValueChange={onProcedureDescriptionChange}
                        disabled={filtersDisabled}
                        value={procedureDescriptionFilterValue}
                        onEnter={() => sideBarStore.applyCurrentFilter()}
                    />
                );
            case 'ModalityIDs':
                return (
                    <DobMultiselect
                        direction="vertical"
                        disabled={filtersDisabled}
                        placeholder=""
                        key={datapoint}
                        label={label}
                        type="filter"
                        onChange={setSelectedModalities}
                        options={allModalitiesMultiSelectOptions}
                        optionsSelected={selectedModalitiesMultiSelectOptions}
                    />
                );
            case 'ProcedureCodes':
                return (
                    <DobMultiselect
                        async
                        direction="vertical"
                        disabled={filtersDisabled}
                        placeholder=""
                        key={datapoint}
                        label={label}
                        type="filter"
                        onChange={setSelectedProcedureCodes}
                        optionsSelected={selectedProcedureCodesMultiSelectOptions}
                        loadOptions={(searchTerm) => worklistFilterHelper.loadProcedureCodes(searchTerm)}
                    />
                );
            case 'OrderNumber':
                return (
                    <DebouncedFilterInput
                        key={datapoint}
                        label={label}
                        onValueChange={onOrderNumberChange}
                        disabled={filtersDisabled}
                        value={orderNumberFilterValue}
                        onEnter={() => sideBarStore.applyCurrentFilter()}
                    />
                );
            case 'RequestingPhysicianFirstName':
                return (
                    <DebouncedFilterInput
                        key={datapoint}
                        label={label}
                        onValueChange={onRequestingPhysicianFirstNameChange}
                        disabled={filtersDisabled}
                        value={requestingPhysicianFirstNameFilterValue}
                        onEnter={() => sideBarStore.applyCurrentFilter()}
                    />
                );
            case 'RequestingPhysicianLastName':
                return (
                    <DebouncedFilterInput
                        key={datapoint}
                        label={label}
                        onValueChange={onRequestingPhysicianLastNameChange}
                        disabled={filtersDisabled}
                        value={requestingPhysicianLastNameFilterValue}
                        onEnter={() => sideBarStore.applyCurrentFilter()}
                    />
                );
            default:
                return assertUnreachable(datapoint);
        }
    };

    const renderReportFilter = (datapoint: ReportFilterProperty, label: string): React.ReactElement => {
        switch (datapoint) {
            case 'InvoiceStatuses':
                return (
                    <DobMultiselect
                        direction="vertical"
                        disabled={filtersDisabled}
                        placeholder=""
                        key={datapoint}
                        label={label}
                        type="filter"
                        onChange={setSelectedInvoiceStatuses}
                        options={allInvoiceStatusesMultiSelectOptions}
                        optionsSelected={selectedInvoiceStatusesMultiSelectOptions}
                    />
                );
            case 'ReadyForInvoicing':
                return (
                    <DobSelect
                        key={datapoint}
                        optionSelected={readyForInvoicingOptions.find((x) => x.value === readyForInvoicingFilterValue)}
                        onChange={handleReadyForInvoicingChange}
                        options={readyForInvoicingOptions}
                        disabled={filtersDisabled}
                        type="filter"
                        label={t('ReadyForInvoicing')}
                    />
                );
            case 'ReportLocked':
                return (
                    <DobSelect
                        key={datapoint}
                        optionSelected={reportLockedOptions.find((x) => x.value === reportLockedFilterValue)}
                        onChange={handleReportLockedChange}
                        options={reportLockedOptions}
                        disabled={filtersDisabled}
                        type="filter"
                        label={label}
                    />
                );
            case 'Justification':
                return (
                    <DobSelect
                        key={datapoint}
                        optionSelected={justificationOptions.find((x) => x.value === justificationFilterValue)}
                        onChange={handleJustificationChange}
                        options={justificationOptions}
                        disabled={filtersDisabled}
                        type="filter"
                        label={label}
                    />
                );
            case 'ReportAvailable':
                return (
                    <DobSelect
                        key={datapoint}
                        optionSelected={reportAvailableOptions.find((x) => x.value === reportAvailableFilterValue)}
                        onChange={handleReportAvailableChange}
                        options={reportAvailableOptions}
                        disabled={filtersDisabled}
                        type="filter"
                        label={label}
                    />
                );
            case 'ReportStatuses':
                return (
                    <DobMultiselect
                        direction="vertical"
                        disabled={filtersDisabled}
                        placeholder=""
                        key={datapoint}
                        label={label}
                        type="filter"
                        onChange={setSelectedReportStatuses}
                        options={allReportStatusesMultiSelectOptions}
                        optionsSelected={selectedReportStatusesMultiSelectOptions}
                    />
                );
            case 'ReportModifiedBy':
                return (
                    <DobMultiselect
                        async
                        direction="vertical"
                        disabled={filtersDisabled}
                        placeholder=""
                        key={datapoint}
                        label={label}
                        type="filter"
                        onChange={setSelectedReportModifiedBy}
                        optionsSelected={selectedReportModifiedByMultiSelectOptions}
                        loadOptions={(searchTerm) => worklistFilterHelper.loadReportModifiedByUsers(searchTerm)}
                    />
                );
            case 'ReportCreatedDateRange':
                return (
                    <StudyDateFilter
                        filterIsActive={sideBarStore.selectedSpecificFilter === 'ReportCreatedDateRange'}
                        label={label}
                        key={datapoint}
                        selectedFromDate={reportCreatedFrom}
                        selectedUntilDate={reportCreatedUntil}
                        selectedPreset={reportCreatedDateRangePreset}
                        onChange={(update) => worklistFilterHelper.setReportCreatedDateRangeUpdate(update)}
                        setStudyDateFilterActive={(isActive) => sideBarStore.setSpecificFilter(isActive ? 'ReportCreatedDateRange' : undefined)}
                    />
                );
            case 'ReportModifiedDateRange':
                return (
                    <StudyDateFilter
                        filterIsActive={sideBarStore.selectedSpecificFilter === 'ReportModifiedDateRange'}
                        label={label}
                        key={datapoint}
                        selectedFromDate={reportModifiedFrom}
                        selectedUntilDate={reportModifiedUntil}
                        selectedPreset={reportModifiedDateRangePreset}
                        onChange={(update) => worklistFilterHelper.setReportModifiedDateRangeUpdate(update)}
                        setStudyDateFilterActive={(isActive) => sideBarStore.setSpecificFilter(isActive ? 'ReportModifiedDateRange' : undefined)}
                    />
                );
            case 'ReportParticipants':
                return (
                    <DobMultiselect
                        async
                        direction="vertical"
                        disabled={filtersDisabled}
                        placeholder=""
                        key={datapoint}
                        label={label}
                        type="filter"
                        onChange={setSelectedReportParticipants}
                        optionsSelected={selectedReportParticipantsOptions}
                        loadOptions={(searchTerm) => worklistFilterHelper.loadReportParticipants(searchTerm)}
                    />
                );
            case 'ReportParticipantType':
                return (
                    <DobMultiselect
                        direction="vertical"
                        disabled={filtersDisabled}
                        placeholder=""
                        key={datapoint}
                        label={label}
                        type="filter"
                        onChange={setSelectedReportParticipantTypes}
                        options={allReportParticipantTypeOptions}
                        optionsSelected={selectedReportParticipantTypeOptions}
                    />
                );
            case 'Transcriptionists':
                return (
                    <DobMultiselect
                        async
                        direction="vertical"
                        disabled={filtersDisabled}
                        placeholder=""
                        key={datapoint}
                        label={label}
                        type="filter"
                        onChange={setSelectedTranscriptionists}
                        optionsSelected={selectedTranscriptionistsMultiSelectOptions}
                        loadOptions={(searchTerm) => worklistFilterHelper.loadTranscriptionists(searchTerm)}
                    />
                );
            default:
                return assertUnreachable(datapoint);
        }
    };

    const renderFilterOfType = (datapoint: WorklistFilterProperty): React.ReactElement => {
        const label = getTitleForWorklistProperty(datapoint);

        if (isPatientFilterProperty(datapoint)) {
            return renderPatientFilter(datapoint, label);
        }
        if (isOrderFilterProperty(datapoint)) {
            return renderOrderFilter(datapoint, label);
        }
        return renderReportFilter(datapoint, label);
    };

    const visibleFilters = worklistViewConfig.Filters.filter((f) => f.Visible);
    const filters = visibleFilters.map((f) => renderFilterOfType(f.ID));
    const selectedFilter = sideBarStore.selectedSpecificFilter ? renderFilterOfType(sideBarStore.selectedSpecificFilter) : null;

    return <div className={filterInputsStyle}>{selectedFilter ?? filters.map((x) => x)}</div>;
};

export default observer(WorklistFilters);
